/**
 * Custom hook for timeline and priority cases
 */
import { useMemo } from 'react';
import type { TimelineItem, CaseSummary } from '../types';

interface UseTimelineProps {
  events: any[];
  allCases: any[];
  currentTime: Date;
  language: string;
  t: (en: string, no: string) => string;
}

export const useTimeline = ({ events, allCases, currentTime, language, t }: UseTimelineProps) => {
  // Generate mock events for testing if no events exist
  const testEvents = useMemo(() => {
    if (events.length > 0) return events;
    
    const now = new Date();
    
    const mockEvents = [
      {
        id: 'mock-1',
        title: language === 'en' ? 'Team standup' : 'Team standup',
        start_time: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 0).toISOString(),
        location: 'Teams'
      },
      {
        id: 'mock-2',
        title: language === 'en' ? 'Client call — Telenor' : 'Kundemøte — Telenor',
        start_time: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 11, 30).toISOString(),
        location: 'Teams'
      },
      {
        id: 'mock-3',
        title: language === 'en' ? 'Contract review' : 'Kontraktsgjennomgang',
        start_time: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 14, 0).toISOString(),
        location: language === 'en' ? 'Meeting room 2' : 'Møterom 2'
      },
      {
        id: 'mock-4',
        title: language === 'en' ? 'Weekly planning' : 'Ukeplanlegging',
        start_time: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 16, 0).toISOString(),
        location: 'Teams'
      }
    ];
    
    return mockEvents;
  }, [events, language]);

  // Build timeline for today
  const todayTimeline = useMemo((): TimelineItem[] => {
    const now = currentTime;
    const today = now.toDateString();
    const items: TimelineItem[] = [];
    
    // Add events/meetings from testEvents
    testEvents.forEach((event: any) => {
      const eventDate = new Date(event.start_time || event.date);
      if (eventDate.toDateString() === today) {
        const time = eventDate.toLocaleTimeString(language === 'en' ? 'en-US' : 'nb-NO', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        });
        items.push({
          id: event.id,
          time,
          title: event.title || event.subject || t('Meeting', 'Møte'),
          subtitle: event.location || event.attendees?.[0]?.name,
          type: 'meeting',
          isPast: eventDate < now,
          url: event.teams_link || event.meeting_link
        });
      }
    });
    
    // Add case deadlines for today
    allCases.forEach((c: any) => {
      if (c.deadline) {
        const deadlineDate = new Date(c.deadline);
        if (deadlineDate.toDateString() === today) {
          items.push({
            id: c.id,
            time: deadlineDate.toLocaleTimeString(language === 'en' ? 'en-US' : 'nb-NO', { 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: false 
            }),
            title: c.topic || t('Untitled', 'Uten tittel'),
            subtitle: c.client_name,
            type: 'deadline',
            urgent: true,
            isPast: deadlineDate < now,
            url: `/lexy/case/${c.id}/workspace`
          });
        }
      }
    });
    
    return items.sort((a, b) => a.time.localeCompare(b.time));
  }, [testEvents, allCases, currentTime, language, t]);

  // Get priority cases
  const priorityCases = useMemo((): CaseSummary[] => {
    const now = currentTime;
    const today = now.toDateString();
    const active = allCases?.filter((c: any) => {
      if (c.status === 'COMPLETED') return false;
      if (c.deadline && new Date(c.deadline).toDateString() === today) return false;
      return true;
    }) || [];
    
    return [...active]
      .sort((a: any, b: any) => {
        const priorityOrder: Record<string, number> = { 'CRITICAL': 0, 'HIGH': 1, 'MEDIUM': 2, 'LOW': 3 };
        return (priorityOrder[a.priority] ?? 3) - (priorityOrder[b.priority] ?? 3);
      })
      .slice(0, 3)
      .map((c: any) => {
        let deadlineText = '';
        if (c.deadline) {
          const daysUntil = Math.ceil((new Date(c.deadline).getTime() - now.getTime()) / 86400000);
          if (daysUntil === 1) deadlineText = t('Tomorrow', 'I morgen');
          else if (daysUntil <= 7 && daysUntil > 1) deadlineText = t(`${daysUntil} days`, `${daysUntil} dager`);
        }
        
        return {
          id: c.id,
          title: c.topic || t('Untitled', 'Uten tittel'),
          client: c.client_name || '',
          priority: c.priority,
          status: c.status,
          deadline: c.deadline,
          deadlineText
        };
      });
  }, [allCases, currentTime, t]);

  return {
    testEvents,
    todayTimeline,
    priorityCases,
  };
};
